// Hybrid data store: localStorage for instant reads, Firestore for cloud persistence
// All writes go to both. On app load, Firestore data syncs down to localStorage.

import { db } from './firebase'
import {
  collection, doc, setDoc, getDoc, getDocFromServer, getDocs, getDocsFromServer, query, where, writeBatch, deleteDoc, updateDoc, orderBy
} from 'firebase/firestore'
import * as Sentry from '@sentry/react'
import { restGet, restQuery, restSet, restBatchSet, restDelete as restDeleteDoc, restDeleteByQuery, restDedup, waitForAuth } from './firestoreRest'
import { getProducts } from '../data/products'
import { BAKERY_PRODUCT_CODE_ALIASES, BAKERY_PRODUCTS_SEED } from '../data/bakeryData'
import { YAWEHS_PRODUCTS_SEED } from '../data/yawehsData'

// Canonical product seed for a bakery-family department. Source of truth
// shipped with the deploy — always at least as fresh as the Firestore
// mirror, which can lag behind code releases when seedIfNeeded misses an
// update. Used as a fallback for code-validation lookups so a stale
// products cache cannot make valid canonical codes look "unknown".
function getCanonicalSeedForDept(dept) {
  if (dept === 'yawehs') return YAWEHS_PRODUCTS_SEED
  if (dept === 'bakery') return BAKERY_PRODUCTS_SEED
  return null
}

// Returns the merged product list a bakery code lookup should consult:
// the live cache (custom products + Firestore mirror) UNION the canonical
// seed shipped with this build. The seed entries fill in any canonical
// codes the cache is missing because of a stale `products/__meta.seeded`
// flag. Cache entries still take precedence — they may carry custom
// prices or other dynamic edits.
function getProductsForLookup(dept) {
  const live = getProducts(dept) || []
  const seed = getCanonicalSeedForDept(dept)
  if (!seed) return live
  const liveCodes = new Set(live.map(p => p.code))
  const merged = [...live]
  for (const p of seed) {
    if (!liveCodes.has(p.code)) merged.push(p)
  }
  return merged
}

// Auto-log weather when sales are saved (fire-and-forget)
function triggerWeatherLog() {
  import('./weather').then(m => m.autoLogTodayWeather()).catch(() => {})
}

// ─── Local cache (localStorage) ───

const STORAGE_KEYS = {
  morningLog: 'desres_morning_log',
  surplus: 'desres_surplus',
  sales: 'desres_sales',
  flags: 'desres_flags',
  dailySummary: 'desres_daily_summary',
  dailyCategorySummary: 'desres_daily_cat_summary',
  monthlySummary: 'desres_monthly_summary',
  auditLog: 'desres_audit_log',
  products: 'desres_products',
  weatherHistory: 'desres_weather_history',
}

// One-time localStorage purge — bump version to re-trigger
const PURGE_VERSION = 'v13-clean-flags'
if (localStorage.getItem('desres_purge') !== PURGE_VERSION) {
  // Nuclear option: clear ALL localStorage and all IndexedDB databases
  localStorage.clear()
  localStorage.setItem('desres_purge', PURGE_VERSION)
  // Also wipe IndexedDB databases that Firebase uses
  if (window.indexedDB?.databases) {
    window.indexedDB.databases().then(dbs => {
      for (const d of dbs) {
        if (d.name) window.indexedDB.deleteDatabase(d.name)
      }
    }).catch(() => {})
  }
  console.log('Purged ALL localStorage + IndexedDB')
}

function load(key) {
  try {
    const data = localStorage.getItem(key)
    return data ? JSON.parse(data) : []
  } catch { return [] }
}

function save(key, data) {
  localStorage.setItem(key, JSON.stringify(data))
}

function genId() {
  return crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2)
}

// ─── Firestore helpers ───

// Map local storage keys to Firestore collection names
const COLLECTIONS = {
  morningLog: 'morningLogs',
  surplus: 'surplus',
  sales: 'sales',
  flags: 'flags',
  dailySummary: 'dailySummaries',
  dailyCategorySummary: 'dailyCategorySummaries',
  monthlySummary: 'monthlySummaries',
  auditLog: 'auditLog',
  products: 'products',
  weatherHistory: 'weatherHistory',
}

// Firestore REST write helpers moved to firestoreWriteHelpers.js in
// Step 4.2. Re-imported here so the rest of store.js keeps working
// without needing import-path changes across the module.
import {
  firestoreSet, firestoreBatchSet, firestoreDelete, firestoreUpdate,
} from './firestoreWriteHelpers'

// ─── Sync: pull Firestore → localStorage on app load ───

let syncComplete = false
let syncCallbacks = []

export function onSyncComplete(cb) {
  if (syncComplete) { cb(); return }
  syncCallbacks.push(cb)
}

export async function syncFromFirestore() {
  // Skip full-collection sync — backgroundSyncForDate handles per-date sync via REST.
  // The old full-collection restList calls fail with 400 on large collections.
  syncComplete = true
  syncCallbacks.forEach(cb => cb())
  syncCallbacks = []
  console.log('Firestore sync complete (per-date sync handles data loading)')
}

// ─── Morning Log ───

// Sync version: reads from localStorage only (used by callers that can't await)
export function getMorningLogs(department, date) {
  const all = load(STORAGE_KEYS.morningLog)
  return all.filter(r => r.departmentId === department && (!date || r.date === date))
}

// ─── Vors-with-Steak daily toggle (kitchen only) ───
// Per-day flag describing how steak is being served today. The historical
// boolean toggle (vorsWithSteak true/false) was replaced with a 3-option
// enum so the kitchen can track an "extra steak, no vors" plate variant
// in addition to "with vors" and "no vors". The function names below were
// kept for minimum blast radius — they now operate on the string enum.
//
// Stored values:
//   'vors'         — default, full plate has 2 steak + 1 vors (legacy 'true')
//   'no_vors'      — vors not served, full plate is 2.5 steak (legacy 'false')
//   'extra_steak'  — vors not served AND plate is rebalanced to 3 steak
//
// Backward compatibility: legacy boolean values ('true' / 'false' from
// localStorage, or actual booleans from Firestore) are normalised on read
// so existing devices keep working without a forced migration.

function vorsWithSteakKey(department, date) {
  return `desres_vors_with_steak_${department}_${date}`
}

function papWarmersKey(department, date) {
  return `desres_pap_warmers_${department}_${date}`
}

// Normalise any stored value (string enum or legacy boolean) to the
// current 3-option enum, or null if the value isn't recognised.
function normaliseSteakMode(raw) {
  if (raw === 'vors' || raw === 'no_vors' || raw === 'extra_steak') return raw
  if (raw === 'true' || raw === true) return 'vors'
  if (raw === 'false' || raw === false) return 'no_vors'
  return null
}

// Sync read from localStorage. Returns the steak-mode enum string or null
// if the key is missing. Legacy 'true'/'false' values are translated.
export function getVorsWithSteak(department, date) {
  try {
    const v = localStorage.getItem(vorsWithSteakKey(department, date))
    if (v === null || v === undefined) return null
    return normaliseSteakMode(v)
  } catch { return null }
}

// Round to 1 decimal place — "8.5 warmers" is allowed; "8.55" isn't.
function _round1dp(n) {
  return Math.round(Number(n) * 10) / 10
}

// Sync read from localStorage. Returns a non-negative number (possibly
// fractional — staff can prep half a warmer and enter 8.5) or null if
// the key is missing / unparseable.
export function getPapWarmers(department, date) {
  try {
    const v = localStorage.getItem(papWarmersKey(department, date))
    if (v === null || v === undefined || v === '') return null
    const n = Number(v)
    if (!Number.isFinite(n) || n < 0) return null
    return _round1dp(n)
  } catch { return null }
}

// Build a complete meta doc from whatever is currently in localStorage,
// optionally overriding individual fields. This is the merge-safe primitive
// underneath setVorsWithSteak and setPapWarmers — every write composes the
// full doc so a partial setter never clobbers a sibling field.
function _composeMetaDoc(department, date, override = {}) {
  const steakMode = getVorsWithSteak(department, date) ?? 'vors'
  const papWarmers = getPapWarmers(department, date)
  const doc = {
    id: `${department}_${date}_meta`,
    departmentId: department,
    date,
    type: 'meta',
    // Field name is intentionally kept as `vorsWithSteak` so existing
    // Firestore meta docs don't need migration. The value is a string
    // enum instead of a boolean. Read-side normalisation handles both.
    vorsWithSteak: steakMode,
    ...(papWarmers != null ? { papWarmers } : {}),
    ...override,
    updatedAt: new Date().toISOString(),
  }
  return doc
}

// Write to localStorage immediately and fire-and-forget Firestore meta doc.
// The meta doc has no `category` field, so it is filtered out by the morning
// log loaders at backgroundSyncForDate (which require rec.category to be set).
//
// Accepts the enum string directly. Legacy boolean callers that still pass
// `true`/`false` are normalised so the storage layer is forgiving. The
// underlying _composeMetaDoc helper merges the new vorsWithSteak with any
// existing papWarmers value so this write does not clobber the sibling.
export function setVorsWithSteak(department, date, value) {
  const mode = normaliseSteakMode(value) || 'vors'
  try { localStorage.setItem(vorsWithSteakKey(department, date), mode) } catch {}
  firestoreSet(COLLECTIONS.morningLog, _composeMetaDoc(department, date, { vorsWithSteak: mode }))
}

// Write the pap warmers count for the day. Same merge-safe pattern as
// setVorsWithSteak — the underlying _composeMetaDoc reads the current
// vorsWithSteak from localStorage so we never overwrite the sibling.
//
// Accepts any value coercible to a non-negative number with up to 1
// decimal of precision (half-warmer granularity). Negative or
// non-numeric values are coerced to 0 to keep the storage layer forgiving.
export function setPapWarmers(department, date, value) {
  const n = Number(value)
  const safe = Number.isFinite(n) && n >= 0 ? _round1dp(n) : 0
  try { localStorage.setItem(papWarmersKey(department, date), String(safe)) } catch {}
  firestoreSet(COLLECTIONS.morningLog, _composeMetaDoc(department, date, { papWarmers: safe }))
}

// Ensure the localStorage cache that getDailyPrices reads is populated for
// a given dept+date. Used as an on-demand pre-fetch from any view that
// needs reconciliation prices but can't await on the synchronous
// getDailyPrices read path.
//
// Strategy:
//   1. If both steak + vors keys are already in localStorage, no-op.
//   2. Otherwise, query the morningLogs collection for the date and
//      extract pieceCost from steak/vors per-category docs and steakMode
//      from the meta doc, then write to localStorage.
//
// This is the cross-device recovery path: a fresh device that has never
// run saveMorningLog locally for this date can still get the correct
// prices into its cache before reconciliation runs. Callers (e.g.
// ReconciliationReport.handleRunReconciliation) should `await` this
// before calling buildFullReconciliation.
export async function ensureDailyPricesLoaded(dept, date) {
  if (!dept || !date) return
  const steakKey = `desres_steak_unit_price_${dept}_${date}`
  const vorsKey = `desres_vors_unit_price_${dept}_${date}`
  const modeKey = `desres_vors_with_steak_${dept}_${date}`

  // Skip if both prices are already in the cache. We don't gate on the
  // mode key — that's cheap to refresh and small enough not to matter.
  let steakCached = null
  let vorsCached = null
  try {
    steakCached = localStorage.getItem(steakKey)
    vorsCached = localStorage.getItem(vorsKey)
  } catch { /* localStorage unavailable */ }
  if (steakCached && vorsCached) return

  try {
    const docs = await restQuery(COLLECTIONS.morningLog, 'date', date)
    const relevant = docs.filter(d => d.departmentId === dept)
    const steakDoc = relevant.find(d => d.category === 'steak')
    const vorsDoc = relevant.find(d => d.category === 'vors')
    const metaDoc = relevant.find(d => d.type === 'meta')

    try {
      if (!steakCached && steakDoc?.pieceCost > 0) {
        localStorage.setItem(steakKey, String(steakDoc.pieceCost))
      }
      if (!vorsCached && vorsDoc?.pieceCost > 0) {
        localStorage.setItem(vorsKey, String(vorsDoc.pieceCost))
      }
      if (metaDoc && metaDoc.vorsWithSteak != null) {
        const mode = normaliseSteakMode(metaDoc.vorsWithSteak)
        if (mode) localStorage.setItem(modeKey, mode)
      }
      // Hydrate the papWarmers cache opportunistically — same fetch,
      // no extra round trip. Per-key guard so we don't overwrite a
      // value the local user just saved.
      if (metaDoc && metaDoc.papWarmers != null) {
        const existingPapWarmers = localStorage.getItem(papWarmersKey(dept, date))
        if (!existingPapWarmers) {
          const n = Number(metaDoc.papWarmers)
          if (Number.isFinite(n) && n >= 0) {
            localStorage.setItem(papWarmersKey(dept, date), String(_round1dp(n)))
          }
        }
      }
    } catch { /* localStorage write failure — non-fatal */ }
  } catch (err) {
    console.warn('[ensureDailyPricesLoaded] failed:', err?.message)
    Sentry.captureException(err, { extra: { dept, date, fn: 'ensureDailyPricesLoaded' } })
  }
}

// Async read: prefer localStorage; if absent, try Firestore meta doc; else null.
// Both string-enum and legacy-boolean values from Firestore are accepted.
// Side effect: if the fetched meta doc carries papWarmers, that value is
// also written to its localStorage cache so a single Firestore round-trip
// hydrates both meta fields at once.
export async function fetchVorsWithSteak(department, date) {
  const local = getVorsWithSteak(department, date)
  if (local !== null) return local
  try {
    const id = `${department}_${date}_meta`
    const doc = await restGet(`${COLLECTIONS.morningLog}/${id}`)
    if (doc) {
      // Opportunistically hydrate the papWarmers cache while we have the doc.
      if (doc.papWarmers != null) {
        const n = Number(doc.papWarmers)
        if (Number.isFinite(n) && n >= 0) {
          try { localStorage.setItem(papWarmersKey(department, date), String(_round1dp(n))) } catch {}
        }
      }
      if (doc.vorsWithSteak !== undefined && doc.vorsWithSteak !== null) {
        const mode = normaliseSteakMode(doc.vorsWithSteak)
        if (mode === null) return null
        try { localStorage.setItem(vorsWithSteakKey(department, date), mode) } catch {}
        return mode
      }
    }
  } catch {}
  return null
}

// Async fetch for pap warmers — mirrors fetchVorsWithSteak. Reads
// localStorage first, falls back to the Firestore meta doc, and
// opportunistically hydrates the vorsWithSteak cache from the same
// fetch result so callers don't need two round trips.
export async function fetchPapWarmers(department, date) {
  const local = getPapWarmers(department, date)
  if (local !== null) return local
  try {
    const id = `${department}_${date}_meta`
    const doc = await restGet(`${COLLECTIONS.morningLog}/${id}`)
    if (doc) {
      // Opportunistically hydrate vorsWithSteak from the same fetch.
      if (doc.vorsWithSteak !== undefined && doc.vorsWithSteak !== null) {
        const mode = normaliseSteakMode(doc.vorsWithSteak)
        if (mode) {
          try { localStorage.setItem(vorsWithSteakKey(department, date), mode) } catch {}
        }
      }
      if (doc.papWarmers != null) {
        const n = Number(doc.papWarmers)
        if (Number.isFinite(n) && n >= 0) {
          const rounded = _round1dp(n)
          try { localStorage.setItem(papWarmersKey(department, date), String(rounded)) } catch {}
          return rounded
        }
      }
    }
  } catch {}
  return null
}

// Get the most recent piece cost for a category from previous morning log entries.
// Step A: checks localStorage first (fast). Step B: falls back to Firestore REST if localStorage empty.
export async function getLastPieceCost(dept, category, beforeDate) {
  // Step A: localStorage
  const all = load(STORAGE_KEYS.morningLog)
  const localCandidates = all.filter(r =>
    r.departmentId === dept &&
    r.category === category &&
    r.date < beforeDate &&
    r.pieceCost > 0
  )
  if (localCandidates.length > 0) {
    localCandidates.sort((a, b) => b.date.localeCompare(a.date))
    return localCandidates[0].pieceCost
  }

  // Step B: Firestore REST fallback
  try {
    const docs = await restQuery(COLLECTIONS.morningLog, 'category', category)
    const qualifying = docs.filter(d =>
      d.departmentId === dept &&
      d.pieceCost > 0 &&
      d.date < beforeDate
    )
    if (qualifying.length === 0) return null
    qualifying.sort((a, b) => b.date.localeCompare(a.date))
    return qualifying[0].pieceCost
  } catch (err) {
    console.warn('[getLastPieceCost] Firestore fallback failed:', err?.message)
    return null
  }
}

// Background sync: pulls data from Firestore into localStorage for a given date.
// Always fetches from Firestore regardless of localStorage state (Firestore is source of truth).
// Uses Firestore REST API (bypasses broken SDK connection).
// Returns true if new data was written (caller should re-read localStorage and re-render).
const _syncedDates = new Set()
export async function backgroundSyncForDate(department, date) {
  if (!date) return false

  // Wait for auth — skip sync entirely if not logged in
  const user = await waitForAuth()
  if (!user) {
    console.log('[SYNC] Skipping sync — user not authenticated')
    return false
  }

  const key = `${department}_${date}`
  if (_syncedDates.has(key)) return false
  _syncedDates.add(key)

  let changed = false
  const isBakeryDept = department === 'bakery' || department === 'yawehs'

  // Helper: hard-replace localStorage records for this dept+date with Firestore data.
  // Firestore is source of truth — remove any localStorage records for the same
  // dept+date that aren't in the Firestore result set.
  function replaceLocalForDate(storageKey, firestoreRecords, belongsToThisSync) {
    const all = load(storageKey)
    // Remove ALL localStorage records that belong to this dept+date
    const kept = all.filter(r => !belongsToThisSync(r))
    // Replace with exactly the Firestore records
    const merged = [...kept, ...firestoreRecords]
    // Always save — Firestore is source of truth
    save(storageKey, merged)
    // Report changed if record count differs (duplicates removed or new data added)
    return true
  }

  // --- Morning logs ---
  if (!isBakeryDept) {
    try {
      let entries = []
      // Try department-specific doc first, then date-only doc
      for (const docId of [`${department}_${date}`, date]) {
        try {
          const d = await restGet(`morningLogs/${docId}`)
          if (d && d.entries && d.entries.length > 0) {
            console.log('[SYNC] morningLogs/' + docId, 'found', d.entries.length, 'entries')
            entries = d.entries.map(e => ({
              category: e.categoryCode,
              value: Number(e.rawValue ?? 0),
              projectedRandValue: e.projectedRandValue,
              type: e.type,
            }))
            break
          }
        } catch { /* doc doesn't exist or 404 */ }
      }
      // Also check morningLogs collection for per-record docs (saved by saveMorningLog).
      // We hold on to the full restQuery result for two reasons:
      //   1. So we can also extract the meta doc (vorsWithSteak) below — it
      //      lives in the same query result but has no `category` field.
      //   2. So we can preserve `pieceCost` and `openingPieces` from each
      //      synced doc into the local entries shape (Fix 1+2 below).
      let colRecords = []
      try {
        colRecords = await restQuery('morningLogs', 'date', date)
        console.log('[SYNC] morningLogs collection query:', colRecords.length, 'docs')
        for (const rec of colRecords) {
          if (rec.departmentId === department && rec.category) {
            if (!entries.find(e => e.category === rec.category)) {
              // Preserve pieceCost + openingPieces from the Firestore doc so
              // they reach localStorage. Without this, cross-device users
              // never see staff-entered piece prices and reconciliation
              // silently falls back to the legacy R30/R18.
              entries.push({
                category: rec.category,
                value: Number(rec.value ?? 0),
                type: rec.type,
                ...(rec.pieceCost > 0
                  ? { pieceCost: rec.pieceCost, openingPieces: rec.openingPieces }
                  : {}),
              })
            }
          }
        }
      } catch (e) {
        console.warn('[SYNC] morningLogs collection query error:', e?.message)
        Sentry.captureException(e, { extra: { fn: 'backgroundSyncForDate.morningLogs_query', department, date } })
      }

      console.log('[SYNC] morning log entries:', entries.length, entries.map(e => e.category))
      if (entries.length > 0) {
        const all = load(STORAGE_KEYS.morningLog)
        let localChanged = false
        for (const r of entries) {
          const existing = all.find(x => x.departmentId === department && x.date === date && x.category === r.category)
          if (!existing) {
            all.push({ id: genId(), departmentId: department, date, ...r })
            localChanged = true
          } else if (
            r.value !== existing.value ||
            (r.pieceCost > 0 && !(existing.pieceCost > 0))
          ) {
            // Existing-record update path. We propagate `value` from Firestore
            // (sync source of truth for the rand value), but pieceCost is
            // ONLY filled in when the local record has none. Local saves are
            // authoritative for pieceCost on this device — if a user just
            // corrected a steak entry locally, sync must not regress that
            // correction with a stale Firestore pieceCost.
            existing.value = r.value
            if (r.projectedRandValue !== undefined) existing.projectedRandValue = r.projectedRandValue
            if (r.pieceCost > 0 && !(existing.pieceCost > 0)) {
              existing.pieceCost = r.pieceCost
              existing.openingPieces = r.openingPieces
            }
            localChanged = true
          }
        }
        if (localChanged) { save(STORAGE_KEYS.morningLog, all); changed = true }
      }

      // Fix 3 — extract steak/vors unit prices from the synced morning log
      // records into the localStorage cache that getDailyPrices reads. This
      // is the bridge between the cross-device Firestore source of truth
      // and the per-device sync cache the reconciliation engine consumes.
      //
      // Per-key guard: ONLY populate the cache when it's missing. Same
      // policy as ensureDailyPricesLoaded — sync fills in missing keys but
      // never overwrites a value that was set locally by a recent
      // saveMorningLog call. Without this guard, a stale Firestore record
      // would silently regress a corrected local cache value.
      try {
        const allMorningLogs = load(STORAGE_KEYS.morningLog)
        const todayLogs = allMorningLogs.filter(r =>
          r.departmentId === department && r.date === date
        )
        const steakLog = todayLogs.find(r => r.category === 'steak')
        const vorsLog = todayLogs.find(r => r.category === 'vors')
        const steakKey = `desres_steak_unit_price_${department}_${date}`
        const vorsKey = `desres_vors_unit_price_${department}_${date}`
        const existingSteakCache = localStorage.getItem(steakKey)
        const existingVorsCache = localStorage.getItem(vorsKey)
        if (!existingSteakCache && steakLog?.pieceCost > 0) {
          localStorage.setItem(steakKey, String(steakLog.pieceCost))
        }
        if (!existingVorsCache && vorsLog?.pieceCost > 0) {
          localStorage.setItem(vorsKey, String(vorsLog.pieceCost))
        }
      } catch { /* localStorage write failure — non-fatal */ }

      // Fix 4 — extract steakMode AND papWarmers from the meta doc that
      // lives alongside the per-category morning log records. The meta doc
      // is filtered out of the entries loop above (it has no `category`
      // field), but its sibling fields still need to reach the localStorage
      // caches so reconciliation and the DailyOps PAP Contribution card
      // pick them up. Per-key guard on each so a stale Firestore field
      // can't overwrite a value the local user just saved.
      try {
        const metaDoc = colRecords.find(r =>
          r.departmentId === department && r.type === 'meta'
        )
        if (metaDoc) {
          if (metaDoc.vorsWithSteak != null) {
            const existingMode = localStorage.getItem(
              `desres_vors_with_steak_${department}_${date}`
            )
            if (!existingMode) {
              const normalisedMode = normaliseSteakMode(metaDoc.vorsWithSteak)
              if (normalisedMode) {
                localStorage.setItem(
                  `desres_vors_with_steak_${department}_${date}`,
                  normalisedMode
                )
              }
            }
          }
          if (metaDoc.papWarmers != null) {
            const existingWarmers = localStorage.getItem(
              papWarmersKey(department, date)
            )
            if (!existingWarmers) {
              const n = Number(metaDoc.papWarmers)
              if (Number.isFinite(n) && n >= 0) {
                localStorage.setItem(
                  papWarmersKey(department, date),
                  String(Math.round(n))
                )
              }
            }
          }
        }
      } catch { /* localStorage write failure — non-fatal */ }
    } catch (err) {
      console.warn('[SYNC] morning logs FAILED:', err?.message)
      Sentry.captureException(err, { extra: { department, date, syncType: 'morningLogs' } })
    }
  }

  // --- Sales ---
  if (!isBakeryDept) {
    try {
      const allSales = await restQuery('sales', 'date', date)
      const deptSales = allSales.filter(s => s.departmentId === department)
      console.log('[SYNC] sales for dept:', deptSales.length)
      if (replaceLocalForDate(STORAGE_KEYS.sales, deptSales,
        r => r.departmentId === department && r.date === date)) {
        changed = true
      }
    } catch (err) {
      console.warn('[SYNC] sales FAILED:', err?.message)
      Sentry.captureException(err, { extra: { department, date, syncType: 'sales' } })
    }
  }

  // --- Surplus ---
  if (!isBakeryDept) {
    try {
      const allSurplus = await restQuery('surplus', 'date', date)
      const deptSurplus = allSurplus.filter(s => s.departmentId === department)
      console.log('[SYNC] surplus for dept:', deptSurplus.length)
      if (replaceLocalForDate(STORAGE_KEYS.surplus, deptSurplus,
        r => r.departmentId === department && r.date === date)) {
        changed = true
      }
    } catch (err) {
      console.warn('[SYNC] surplus FAILED:', err?.message)
      Sentry.captureException(err, { extra: { department, date, syncType: 'surplus' } })
    }
  }

  // --- Bakery: Production, Distribution, Damages, Surplus ---
  if (isBakeryDept) {
    const bakeryTypes = ['production', 'distribution', 'damages', 'surplus']
    for (const type of bakeryTypes) {
      try {
        const colName = bakeryCollection(department, type)
        const records = await restQuery(colName, 'date', date)
        console.log('[SYNC] bakery', type, colName, ':', records.length, 'docs')
        if (replaceLocalForDate(bakeryKey(department, type), records,
          r => r.date === date)) {
          changed = true
        }
      } catch (err) {
        console.warn(`[SYNC] bakery ${type} FAILED:`, err?.message)
        Sentry.captureException(err, { extra: { department, date, syncType: `bakery_${type}` } })
      }
    }

    // Daily Collections
    try {
      const collRecords = await restQuery('bakeryDailyCollections', 'date', date)
      const deptColl = collRecords.filter(r => r.departmentId === department)
      if (replaceLocalForDate('desres_daily_collections', deptColl,
        r => r.departmentId === department && r.date === date)) {
        changed = true
      }
    } catch (err) {
      console.warn('[SYNC] bakery dailyCollections FAILED:', err?.message)
      Sentry.captureException(err, { extra: { department, date, syncType: 'bakeryDailyCollections' } })
    }
  }

  // --- Flags ---
  try {
    const allFlags = await restQuery('flags', 'date', date)
    const deptFlags = allFlags.filter(s => s.departmentId === department)
    if (replaceLocalForDate(STORAGE_KEYS.flags, deptFlags,
      r => r.departmentId === department && r.date === date)) {
      changed = true
    }
  } catch (err) {
    console.warn('[SYNC] flags FAILED:', err?.message)
    Sentry.captureException(err, { extra: { department, date, syncType: 'flags' } })
  }

  // --- Daily summaries (for 30-day avg) ---
  try {
    const allSummaries = await restQuery('dailySummaries', 'departmentId', department)
    console.log('[SYNC] dailySummaries:', allSummaries.length)
    if (replaceLocalForDate(STORAGE_KEYS.dailySummary, allSummaries,
      r => r.departmentId === department)) {
      changed = true
    }
  } catch (err) {
    console.warn('[SYNC] dailySummaries FAILED:', err?.message)
    Sentry.captureException(err, { extra: { department, syncType: 'dailySummaries' } })
  }

  console.log('[SYNC] COMPLETE for', department, date, 'changed:', changed)
  // Notify all components that localStorage was updated by sync
  if (changed && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('desres-sync', { detail: { department, date } }))
  }
  return changed
}

export function saveMorningLog(department, date, category, value, unit, entryMode = 'manual', correctionNote = '', openingPieces = null) {
  const all = load(STORAGE_KEYS.morningLog)
  const existing = all.findIndex(r => r.departmentId === department && r.date === date && r.category === category)
  // Deterministic ID: ensures re-saves overwrite the same Firestore doc instead of creating duplicates
  const deterministicId = `${department}_${date}_${category}`.replace(/\s+/g, '_')
  const computedPieceCost = (openingPieces != null && Number(openingPieces) > 0)
    ? Math.round((Number(value) / Number(openingPieces)) * 100) / 100
    : null
  const record = {
    id: existing >= 0 ? all[existing].id : deterministicId,
    departmentId: department,
    date,
    category,
    value: Number(value),
    unit,
    correctionNote,
    ...(computedPieceCost != null
      ? { openingPieces: Number(openingPieces), pieceCost: computedPieceCost }
      : {}),
    entryMode,
    createdAt: new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(STORAGE_KEYS.morningLog, all)

  // Direct restSet (not the fire-and-forget firestoreSet helper) so we can
  // attach a richer Sentry context on failure. Without this, a silently-
  // failing Firestore write leaves the local cache out of sync with
  // Firestore — and the next backgroundSyncForDate run pulls the stale
  // remote value back into the local record (the bug audit identified for
  // 2026-04-04 steak pieceCost). Sentry now records every failed write
  // with the dept/date/category/recordId so the cause is traceable.
  restSet(`${COLLECTIONS.morningLog}/${record.id}`, record).catch(err => {
    console.warn('[saveMorningLog] Firestore write failed:', err?.message)
    Sentry.captureException(err, {
      extra: {
        fn: 'saveMorningLog',
        category,
        date,
        department,
        recordId: record.id,
      },
    })
  })

  // Cache per-day unit prices for steak and vors so reconciliation can apply
  // dynamic pricing instead of the legacy hardcoded R30/R18 fallback.
  if (computedPieceCost != null) {
    try {
      if (category === 'steak') {
        localStorage.setItem(`desres_steak_unit_price_${department}_${date}`, String(computedPieceCost))
      } else if (category === 'vors') {
        localStorage.setItem(`desres_vors_unit_price_${department}_${date}`, String(computedPieceCost))
      }
    } catch {}
  }

  return record
}

export async function deleteMorningLog(department, date, category) {
  const all = load(STORAGE_KEYS.morningLog)
  const idx = all.findIndex(r => r.departmentId === department && r.date === date && r.category === category)
  if (idx >= 0) {
    const id = all[idx].id
    all.splice(idx, 1)
    save(STORAGE_KEYS.morningLog, all)
    await firestoreDelete(COLLECTIONS.morningLog, id)
    return true
  }
  return false
}

export async function deleteMorningLogBatch(department, date) {
  // 1. Delete from localStorage
  const all = load(STORAGE_KEYS.morningLog)
  const toDelete = all.filter(r => r.departmentId === department && r.date === date)
  const remaining = all.filter(r => !(r.departmentId === department && r.date === date))
  save(STORAGE_KEYS.morningLog, remaining)

  // 2. Delete from Firestore via REST — catches orphaned docs not in localStorage
  try {
    const deleted = await restDeleteByQuery(COLLECTIONS.morningLog, 'date', date,
      d => d.departmentId === department)
    return Math.max(toDelete.length, deleted)
  } catch (err) {
    console.warn('[DELETE] Firestore morning log delete failed:', err.message)
    Sentry.captureException(err, { extra: { department, date, type: 'morningLog' } })
    await Promise.all(toDelete.map(r => firestoreDelete(COLLECTIONS.morningLog, r.id)))
    return toDelete.length
  }
}

// ─── Surplus ───

// Sync version: reads from localStorage only
export function getSurplus(department, date) {
  const all = load(STORAGE_KEYS.surplus)
  return all.filter(r => r.departmentId === department && (!date || r.date === date))
}


export function saveSurplusEntry(department, date, category, value, unit, entryMode = 'manual', surplusPieces = null) {
  const all = load(STORAGE_KEYS.surplus)
  const existing = all.findIndex(r => r.departmentId === department && r.date === date && r.category === category)
  // Deterministic ID: ensures re-saves overwrite the same Firestore doc
  const deterministicId = `surplus_${department}_${date}_${category}`.replace(/\s+/g, '_')
  const record = {
    id: existing >= 0 ? all[existing].id : deterministicId,
    departmentId: department,
    date,
    category,
    value: Number(value),
    unit,
    ...(surplusPieces != null && Number(surplusPieces) > 0 ? { surplusPieces: Number(surplusPieces) } : {}),
    entryMode,
    createdAt: new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(STORAGE_KEYS.surplus, all)
  firestoreSet(COLLECTIONS.surplus, record)
  return record
}

export async function deleteSurplusBatch(department, date) {
  // 1. Delete from localStorage
  const all = load(STORAGE_KEYS.surplus)
  const toDelete = all.filter(r => r.departmentId === department && r.date === date)
  const remaining = all.filter(r => !(r.departmentId === department && r.date === date))
  save(STORAGE_KEYS.surplus, remaining)

  // 2. Delete from Firestore via REST — catches orphaned docs
  try {
    const deleted = await restDeleteByQuery(COLLECTIONS.surplus, 'date', date,
      d => d.departmentId === department)
    return Math.max(toDelete.length, deleted)
  } catch (err) {
    console.warn('[DELETE] Firestore surplus delete failed:', err.message)
    Sentry.captureException(err, { extra: { department, date, type: 'surplus' } })
    for (const r of toDelete) firestoreDelete(COLLECTIONS.surplus, r.id)
    return toDelete.length
  }
}

export async function deleteSalesForDate(department, date, entrySource) {
  // 1. Delete from localStorage
  const all = load(STORAGE_KEYS.sales)
  const toDelete = all.filter(r =>
    r.departmentId === department && r.date === date &&
    (!entrySource || r.source === entrySource || r.entryMode === entrySource)
  )
  const remaining = all.filter(r => !toDelete.includes(r))
  save(STORAGE_KEYS.sales, remaining)

  // 2. Delete from Firestore via REST — query ALL matching docs directly
  //    This catches records that exist in Firestore but not in localStorage
  try {
    const filter = entrySource
      ? (d) => d.departmentId === department && (!entrySource || d.entryMode === entrySource || d.source === entrySource)
      : (d) => d.departmentId === department
    const deleted = await restDeleteByQuery(COLLECTIONS.sales, 'date', date, filter)
    console.log(`[DELETE] Removed ${toDelete.length} local + ${deleted} Firestore sales for ${department}/${date}`)
    return Math.max(toDelete.length, deleted)
  } catch (err) {
    console.warn('[DELETE] Firestore delete failed, fell back to local-only:', err.message)
    Sentry.captureException(err, { extra: { department, date, entrySource, localCount: toDelete.length } })
    // Fallback: at least delete the local IDs from Firestore
    for (const r of toDelete) firestoreDelete(COLLECTIONS.sales, r.id)
    return toDelete.length
  }
}

// ─── Sales Transactions ───

// Sync version: reads from localStorage only
export function getSalesTransactions(department, date) {
  const all = load(STORAGE_KEYS.sales)
  return all.filter(r => r.departmentId === department && (!date || r.date === date))
}


export function getSalesInRange(department, startDate, endDate) {
  const all = load(STORAGE_KEYS.sales)
  return all.filter(r =>
    r.departmentId === department &&
    r.date >= startDate &&
    r.date <= endDate
  )
}

// Deterministic sale document ID: dept_date_productCode
// Ensures re-saves overwrite the same Firestore doc instead of creating duplicates.
function saleDeterministicId(department, date, productCode, index = 0) {
  return `${department}_${date}_${productCode}_${index}`.replace(/\s+/g, '_')
}

export function saveSaleTransaction(department, tx) {
  const all = load(STORAGE_KEYS.sales)
  const dup = all.find(r =>
    r.departmentId === department &&
    r.date === tx.date &&
    r.productCode === tx.productCode
  )
  if (dup) {
    dup.qtySold = tx.qtySold
    dup.salesRands = tx.salesRands
    if (tx.entryMode) dup.entryMode = tx.entryMode
    if (tx.category) dup.category = tx.category
    save(STORAGE_KEYS.sales, all)
    firestoreSet(COLLECTIONS.sales, dup)
    return { record: dup, duplicate: true }
  }
  const record = {
    id: saleDeterministicId(department, tx.date, tx.productCode),
    departmentId: department,
    ...tx,
    createdAt: new Date().toISOString(),
  }
  all.push(record)
  save(STORAGE_KEYS.sales, all)
  firestoreSet(COLLECTIONS.sales, record)
  triggerWeatherLog()
  return { record, duplicate: false }
}

export function saveSalesBatch(department, transactions) {
  const all = load(STORAGE_KEYS.sales)
  const results = []
  const toFirestore = []
  const seen = new Set()
  // Track index per productCode for deterministic IDs
  const codeIndex = {}

  for (const tx of transactions) {
    const idx = codeIndex[tx.productCode] || 0
    codeIndex[tx.productCode] = idx + 1
    const detId = saleDeterministicId(department, tx.date, tx.productCode, idx)

    const existing = all.findIndex(r =>
      r.departmentId === department &&
      r.date === tx.date &&
      r.productCode === tx.productCode
    )
    if (existing >= 0) {
      all[existing].id = detId
      all[existing].qtySold = tx.qtySold
      all[existing].salesRands = tx.salesRands
      if (tx.entryMode) all[existing].entryMode = tx.entryMode
      if (tx.sourceFile) all[existing].sourceFile = tx.sourceFile
      if (tx.category) all[existing].category = tx.category
      if (tx.unitPrice !== undefined) all[existing].unitPrice = tx.unitPrice
      if (!seen.has(detId)) {
        results.push({ record: all[existing], duplicate: true })
        seen.add(detId)
      }
      toFirestore.push(all[existing])
    } else {
      const record = { id: detId, departmentId: department, ...tx, createdAt: new Date().toISOString() }
      all.push(record)
      results.push({ record, duplicate: false })
      seen.add(detId)
      toFirestore.push(record)
    }
  }
  save(STORAGE_KEYS.sales, all)
  firestoreBatchSet(COLLECTIONS.sales, toFirestore)
  triggerWeatherLog()
  return results
}

// Verify saved sales match expected values — detects accumulation bugs
export async function verifySalesForDate(department, date) {
  try {
    const firestoreRecords = await restQuery(COLLECTIONS.sales, 'date', date)
    const deptRecords = firestoreRecords.filter(r => r.departmentId === department)
    const localRecords = load(STORAGE_KEYS.sales).filter(r => r.departmentId === department && r.date === date)

    const warnings = []

    // Check for duplicate productCodes in Firestore
    const codeCounts = {}
    for (const r of deptRecords) {
      codeCounts[r.productCode] = (codeCounts[r.productCode] || 0) + 1
    }
    for (const [code, count] of Object.entries(codeCounts)) {
      if (count > 1) warnings.push(`${code}: ${count} duplicate records in Firestore`)
    }

    // Check if localStorage count differs from Firestore
    if (localRecords.length !== deptRecords.length) {
      warnings.push(`Record count mismatch: localStorage has ${localRecords.length}, Firestore has ${deptRecords.length}`)
    }

    // Check for suspiciously high values (qty or sales 3x+ of what unit price suggests)
    for (const r of deptRecords) {
      if (r.unitPrice && r.qtySold && r.salesRands) {
        const expected = r.unitPrice * r.qtySold
        if (r.salesRands > expected * 2.5 && r.saleType !== 'continuous_mirror') {
          warnings.push(`${r.productCode}: salesRands ${r.salesRands} is ${(r.salesRands / expected).toFixed(1)}x expected (${expected})`)
        }
      }
    }

    return {
      firestoreCount: deptRecords.length,
      localCount: localRecords.length,
      firestoreTotal: deptRecords.reduce((s, r) => s + (Number(r.salesRands) || 0), 0),
      localTotal: localRecords.reduce((s, r) => s + (Number(r.salesRands) || 0), 0),
      warnings,
      ok: warnings.length === 0,
    }
  } catch (err) {
    return { error: err.message, ok: false, warnings: [err.message] }
  }
}

// Expose on window for manual use
if (typeof window !== 'undefined') {
  window.verifySalesForDate = verifySalesForDate
}

export function deleteSaleTransaction(id) {
  const all = load(STORAGE_KEYS.sales)
  save(STORAGE_KEYS.sales, all.filter(r => r.id !== id))
  firestoreDelete(COLLECTIONS.sales, id)
}

export function updateSaleTransaction(id, updates) {
  const all = load(STORAGE_KEYS.sales)
  const idx = all.findIndex(r => r.id === id)
  if (idx >= 0) {
    all[idx] = { ...all[idx], ...updates }
    save(STORAGE_KEYS.sales, all)
    firestoreSet(COLLECTIONS.sales, all[idx])
    return all[idx]
  }
  return null
}

// ─── Flags ───

export function getFlags(department, filters = {}) {
  let all = load(STORAGE_KEYS.flags)
  all = all.filter(r => r.departmentId === department)
  if (filters.date) all = all.filter(r => r.date === filters.date)
  if (filters.startDate) all = all.filter(r => r.date >= filters.startDate)
  if (filters.endDate) all = all.filter(r => r.date <= filters.endDate)
  if (filters.severity) all = all.filter(r => r.severity === filters.severity)
  if (filters.flagCode) all = all.filter(r => r.flagCode === filters.flagCode)
  if (filters.resolved !== undefined) all = all.filter(r => r.resolved === filters.resolved)
  return all
}

// Dedup key for a flag: same type + date + description (or product) = duplicate
function flagKey(f) {
  return `${f.flagCode || f.type || ''}|${f.date || ''}|${f.description || ''}`.toLowerCase()
}

export function saveFlag(department, flag) {
  const all = load(STORAGE_KEYS.flags)
  const key = flagKey(flag)
  const dup = all.find(r => r.departmentId === department && !r.resolved && flagKey(r) === key)
  if (dup) return dup // already exists, skip
  const record = { id: genId(), departmentId: department, resolved: false, ...flag, createdAt: new Date().toISOString() }
  all.push(record)
  save(STORAGE_KEYS.flags, all)
  firestoreSet(COLLECTIONS.flags, record)
  return record
}

export function saveFlagsBatch(department, flags) {
  const all = load(STORAGE_KEYS.flags)
  const records = []
  for (const f of flags) {
    const key = flagKey(f)
    // Check against existing flags AND flags we're about to add
    const dupExisting = all.find(r => r.departmentId === department && !r.resolved && flagKey(r) === key)
    const dupNew = records.find(r => flagKey(r) === key)
    if (dupExisting || dupNew) continue
    const record = { id: genId(), departmentId: department, resolved: false, ...f, createdAt: new Date().toISOString() }
    records.push(record)
  }
  if (records.length > 0) {
    all.push(...records)
    save(STORAGE_KEYS.flags, all)
    firestoreBatchSet(COLLECTIONS.flags, records)
  }
  return records
}

// One-time cleanup: remove duplicate unresolved flags
export function deduplicateFlags(department) {
  const all = load(STORAGE_KEYS.flags)
  const seen = new Map()
  const toDelete = []
  // Sort oldest first so we keep the earliest
  const sorted = [...all].sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''))
  for (const f of sorted) {
    if (f.departmentId !== department || f.resolved) continue
    const key = flagKey(f)
    if (seen.has(key)) {
      toDelete.push(f.id)
    } else {
      seen.set(key, f.id)
    }
  }
  if (toDelete.length === 0) return 0
  const remaining = all.filter(f => !toDelete.includes(f.id))
  save(STORAGE_KEYS.flags, remaining)
  for (const id of toDelete) firestoreDelete(COLLECTIONS.flags, id)
  return toDelete.length
}

export function resolveFlag(id, resolvedBy = 'admin') {
  const all = load(STORAGE_KEYS.flags)
  const idx = all.findIndex(r => r.id === id)
  if (idx >= 0) {
    all[idx].resolved = true
    all[idx].resolvedBy = resolvedBy
    all[idx].resolvedAt = new Date().toISOString()
    save(STORAGE_KEYS.flags, all)
    firestoreSet(COLLECTIONS.flags, all[idx])
  }
}

export function resolveFlagsBulk(ids, resolvedBy = 'admin') {
  const all = load(STORAGE_KEYS.flags)
  const updated = []
  for (const id of ids) {
    const idx = all.findIndex(r => r.id === id)
    if (idx >= 0) {
      all[idx].resolved = true
      all[idx].resolvedBy = resolvedBy
      all[idx].resolvedAt = new Date().toISOString()
      updated.push(all[idx])
    }
  }
  save(STORAGE_KEYS.flags, all)
  if (updated.length > 0) firestoreBatchSet(COLLECTIONS.flags, updated)
}

// ─── Daily Summary ───

export function getDailySummary(department, date) {
  const all = load(STORAGE_KEYS.dailySummary)
  if (date) return all.find(r => r.departmentId === department && r.date === date)
  return all.filter(r => r.departmentId === department)
}

export function saveDailySummary(department, summary) {
  const all = load(STORAGE_KEYS.dailySummary)
  const existing = all.findIndex(r => r.departmentId === department && r.date === summary.date)
  const record = { id: existing >= 0 ? all[existing].id : genId(), departmentId: department, ...summary }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(STORAGE_KEYS.dailySummary, all)
  firestoreSet(COLLECTIONS.dailySummary, record)
  return record
}

// ─── Daily Category Summary ───

export function getDailyCategorySummary(department, date) {
  const all = load(STORAGE_KEYS.dailyCategorySummary)
  return all.filter(r => r.departmentId === department && (!date || r.date === date))
}

export function getDailyCategorySummaryRange(department, startDate, endDate) {
  const all = load(STORAGE_KEYS.dailyCategorySummary)
  return all.filter(r =>
    r.departmentId === department &&
    r.date >= startDate &&
    r.date <= endDate
  )
}

export function saveDailyCategorySummary(department, summary) {
  const all = load(STORAGE_KEYS.dailyCategorySummary)
  const existing = all.findIndex(r =>
    r.departmentId === department && r.date === summary.date && r.category === summary.category
  )
  const record = { id: existing >= 0 ? all[existing].id : genId(), departmentId: department, ...summary }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(STORAGE_KEYS.dailyCategorySummary, all)
  firestoreSet(COLLECTIONS.dailyCategorySummary, record)
  return record
}

// ─── Monthly Summary ───

export function getMonthlySummary(department, year, month) {
  const all = load(STORAGE_KEYS.monthlySummary)
  if (year && month) return all.find(r => r.departmentId === department && r.year === year && r.month === month)
  return all.filter(r => r.departmentId === department)
}

export function saveMonthlySummary(department, summary) {
  const all = load(STORAGE_KEYS.monthlySummary)
  const existing = all.findIndex(r =>
    r.departmentId === department && r.year === summary.year && r.month === summary.month
  )
  const record = { id: existing >= 0 ? all[existing].id : genId(), departmentId: department, ...summary }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(STORAGE_KEYS.monthlySummary, all)
  firestoreSet(COLLECTIONS.monthlySummary, record)
  return record
}

// ─── Audit Log ───

export function getAuditLog(department) {
  const all = load(STORAGE_KEYS.auditLog)
  return all.filter(r => r.departmentId === department).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export function addAuditEntry(department, action, tableName, recordId, oldValue, newValue) {
  const all = load(STORAGE_KEYS.auditLog)
  const record = {
    id: genId(),
    departmentId: department,
    action,
    tableName,
    recordId,
    oldValue,
    newValue,
    performedBy: 'admin',
    createdAt: new Date().toISOString(),
  }
  all.push(record)
  save(STORAGE_KEYS.auditLog, all)
  firestoreSet(COLLECTIONS.auditLog, record)
  return record
}

// ─── Bakery-type departments: Production, Distribution, Damages, Surplus ───
// Used by both 'bakery' (Desres) and 'yawehs' (Yaweh's Delight) departments

function bakeryKey(dept, type) {
  const prefix = dept === 'yawehs' ? 'desres_yawehs' : 'desres_bakery'
  return `${prefix}_${type}`
}

function bakeryCollection(dept, type) {
  const prefix = dept === 'yawehs' ? 'yawehs' : 'bakery'
  return `${prefix}${type.charAt(0).toUpperCase() + type.slice(1)}`
}

export function getProductionLogs(date, shift, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'production'))
  return all.filter(r => (!date || r.date === date) && (!shift || r.shift === shift))
}

export function saveProductionLog(date, shift, productCode, qtyProduced, notes = '', dept = 'bakery') {
  const all = load(bakeryKey(dept, 'production'))
  const existing = all.findIndex(r => r.date === date && r.shift === shift && r.productCode === productCode)
  const detId = `${dept}_${date}_${shift}_${productCode}`.replace(/\s+/g, '_')
  const record = {
    id: existing >= 0 ? all[existing].id : detId,
    departmentId: dept,
    date, shift, productCode,
    qtyProduced: Number(qtyProduced),
    notes,
    createdAt: new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(bakeryKey(dept, 'production'), all)
  firestoreSet(bakeryCollection(dept, 'production'), record)
  return record
}

export function saveProductionBatch(date, shift, entries, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'production'))
  const toFirestore = []
  for (const entry of entries) {
    // Deterministic ID: overwrites on re-save, prevents duplicates
    const detId = `${dept}_${date}_${shift}_${entry.productCode}`.replace(/\s+/g, '_')
    const existing = all.findIndex(r => r.date === date && r.shift === shift && r.productCode === entry.productCode)
    const record = {
      id: existing >= 0 ? all[existing].id : detId,
      departmentId: dept,
      date, shift,
      productCode: entry.productCode,
      qtyProduced: Number(entry.qtyProduced),
      notes: entry.notes || '',
      createdAt: new Date().toISOString(),
    }
    if (existing >= 0) { all[existing].id = detId; all[existing] = { ...record, id: detId } }
    else all.push(record)
    toFirestore.push(record)
  }
  save(bakeryKey(dept, 'production'), all)
  firestoreBatchSet(bakeryCollection(dept, 'production'), toFirestore)
}

// ─── Distribution Log (per distribution point) ───

export function getDistributionLogs(date, pointId, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'distribution'))
  return all.filter(r => (!date || r.date === date) && (!pointId || r.pointId === pointId))
}

export function saveDistributionLog(date, pointId, productCode, qtySent, qtySold, salesRands, dept = 'bakery', unitPrice = 0) {
  const all = load(bakeryKey(dept, 'distribution'))
  const existing = all.findIndex(r => r.date === date && r.pointId === pointId && r.productCode === productCode)
  const detId = `${dept}_${date}_${pointId}_${productCode}`.replace(/\s+/g, '_')
  // Freeze price + projected at save time so historical figures don't drift
  // when product prices change later. unitPrice falls back to the current
  // product list, but the persisted value wins on read. Projected reflects
  // expected COLLECTION (qtySold × price), not qtySent × price — sites
  // return surplus and only owe for what was sold. This single-doc helper
  // doesn't receive reportedSurplus, so projected is calculated from the
  // qtySold parameter directly.
  const frozenUnitPrice = Number(unitPrice || 0)
  const frozenSold = Math.max(0, Number(qtySold || 0))
  const frozenProjected = Math.round(frozenSold * frozenUnitPrice * 100) / 100
  const record = {
    id: existing >= 0 ? all[existing].id : detId,
    departmentId: dept,
    date, pointId, productCode,
    qtySent: Number(qtySent),
    qtySold: Number(qtySold || 0),
    salesRands: Number(salesRands || 0),
    surplus: Number(qtySent) - Number(qtySold || 0),
    unitPrice: frozenUnitPrice,
    projected: frozenProjected,
    createdAt: new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(bakeryKey(dept, 'distribution'), all)
  firestoreSet(bakeryCollection(dept, 'distribution'), record)
  return record
}

export function saveDistributionBatch(date, pointId, entries, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'distribution'))
  const toFirestore = []
  for (const entry of entries) {
    const detId = `${dept}_${date}_${pointId}_${entry.productCode}`.replace(/\s+/g, '_')
    const existing = all.findIndex(r => r.date === date && r.pointId === pointId && r.productCode === entry.productCode)
    // Freeze the unit price + projected total at save time. Callers should
    // resolve the price from the current product list and pass it via
    // entry.unitPrice (or legacy entry.price). If neither is supplied the
    // record stores 0 — the on-screen form's getProducts() fallback still
    // covers the read path for legacy docs.
    //
    // Projected = expected COLLECTION = (qtySent − reportedSurplus) × price.
    // Sites return surplus, so the projected the user owes is for sold-only.
    // The Math.max guard prevents negatives when surplus > sent (data error).
    const frozenUnitPrice = Number(entry.unitPrice ?? entry.price ?? 0)
    const frozenSent = Number(entry.qtySent || 0)
    const frozenSurplus = Number(entry.reportedSurplus || 0)
    const frozenSold = Math.max(0, frozenSent - frozenSurplus)
    const frozenProjected = Math.round(frozenSold * frozenUnitPrice * 100) / 100
    const record = {
      id: existing >= 0 ? all[existing].id : detId,
      departmentId: dept,
      date, pointId,
      productCode: entry.productCode,
      qtySent: Number(entry.qtySent || 0),
      qtySold: Number(entry.qtySold || 0),
      salesRands: Number(entry.salesRands || 0),
      surplus: Number(entry.qtySent || 0) - Number(entry.qtySold || 0),
      reportedSurplus: Number(entry.reportedSurplus || 0),
      damages: Number(entry.damages || 0),
      unitPrice: frozenUnitPrice,
      projected: frozenProjected,
      createdAt: new Date().toISOString(),
    }
    if (existing >= 0) all[existing] = record
    else all.push(record)
    toFirestore.push(record)
  }
  save(bakeryKey(dept, 'distribution'), all)
  firestoreBatchSet(bakeryCollection(dept, 'distribution'), toFirestore)
}

// ─── Damages ───

export function getDamages(date, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'damages'))
  return all.filter(r => !date || r.date === date)
}

export function saveDamage(date, productCode, qty, notes = '', dept = 'bakery') {
  const all = load(bakeryKey(dept, 'damages'))
  const existing = all.findIndex(r => r.date === date && r.productCode === productCode)
  const detId = `${dept}_dmg_${date}_${productCode}`.replace(/\s+/g, '_')
  const record = {
    id: existing >= 0 ? all[existing].id : detId,
    departmentId: dept,
    date, productCode,
    qty: Number(qty),
    notes,
    createdAt: new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(bakeryKey(dept, 'damages'), all)
  firestoreSet(bakeryCollection(dept, 'damages'), record)
  return record
}

export function saveDamagesBatch(date, entries, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'damages'))
  const toFirestore = []
  for (const entry of entries) {
    const detId = `${dept}_dmg_${date}_${entry.productCode}`.replace(/\s+/g, '_')
    const existing = all.findIndex(r => r.date === date && r.productCode === entry.productCode)
    const record = {
      id: existing >= 0 ? all[existing].id : detId,
      departmentId: dept,
      date,
      productCode: entry.productCode,
      qty: Number(entry.qty),
      lossValue: Number(entry.lossValue) || 0,
      notes: entry.notes || '',
      createdAt: new Date().toISOString(),
    }
    if (existing >= 0) all[existing] = record
    else all.push(record)
    toFirestore.push(record)
  }
  save(bakeryKey(dept, 'damages'), all)
  firestoreBatchSet(bakeryCollection(dept, 'damages'), toFirestore)
}

// ─── Surplus (actual surplus per product per date) ───

export function getBakerySurplus(date, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'surplus'))
  return all.filter(r => !date || r.date === date)
}

export function saveBakerySurplusBatch(date, entries, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'surplus'))
  const toFirestore = []
  for (const entry of entries) {
    const existing = all.findIndex(r => r.date === date && r.productCode === entry.productCode)
    const record = {
      id: existing >= 0 ? all[existing].id : genId(),
      departmentId: dept,
      date,
      productCode: entry.productCode,
      actualSurplus: Number(entry.actualSurplus),
      notes: entry.notes || '',
      createdAt: new Date().toISOString(),
    }
    if (existing >= 0) all[existing] = record
    else all.push(record)
    toFirestore.push(record)
  }
  save(bakeryKey(dept, 'surplus'), all)
  firestoreBatchSet(bakeryCollection(dept, 'surplus'), toFirestore)
}

// ─── Bakery Batch Deletes ───

export function deleteProductionForDate(date, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'production'))
  const toDelete = all.filter(r => r.date === date)
  const remaining = all.filter(r => r.date !== date)
  save(bakeryKey(dept, 'production'), remaining)
  for (const r of toDelete) firestoreDelete(bakeryCollection(dept, 'production'), r.id)
  return toDelete.length
}

export function deleteDistributionForDate(date, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'distribution'))
  const toDelete = all.filter(r => r.date === date)
  const remaining = all.filter(r => r.date !== date)
  save(bakeryKey(dept, 'distribution'), remaining)
  for (const r of toDelete) firestoreDelete(bakeryCollection(dept, 'distribution'), r.id)
  return toDelete.length
}

export function deleteDistributionForPoint(date, pointId, dept = 'bakery') {
  const all = load(bakeryKey(dept, 'distribution'))
  const toDelete = all.filter(r => r.date === date && r.pointId === pointId)
  const remaining = all.filter(r => !(r.date === date && r.pointId === pointId))
  save(bakeryKey(dept, 'distribution'), remaining)
  for (const r of toDelete) firestoreDelete(bakeryCollection(dept, 'distribution'), r.id)
  return toDelete.length
}

// ─── Expenditure (Yaweh's Delight specific) ───

const EXPENDITURE_KEY = 'desres_yawehs_expenditure'

export function getExpenditure(date, dept = 'yawehs') {
  const key = dept === 'yawehs' ? EXPENDITURE_KEY : `desres_${dept}_expenditure`
  const all = load(key)
  return all.filter(r => !date || r.date === date)
}

export function saveExpenditureBatch(date, entries, dept = 'yawehs') {
  const key = dept === 'yawehs' ? EXPENDITURE_KEY : `desres_${dept}_expenditure`
  const col = dept === 'yawehs' ? 'yawehsExpenditure' : `${dept}Expenditure`
  const all = load(key)
  const toFirestore = []
  for (const entry of entries) {
    const existing = all.findIndex(r => r.date === date && r.description === entry.description)
    const record = {
      id: existing >= 0 ? all[existing].id : genId(),
      departmentId: dept,
      date,
      description: entry.description || '',
      amount: Number(entry.amount || 0),
      notes: entry.notes || '',
      createdAt: new Date().toISOString(),
    }
    if (existing >= 0) all[existing] = record
    else all.push(record)
    toFirestore.push(record)
  }
  save(key, all)
  firestoreBatchSet(col, toFirestore)
}

// ─── Cash on Hand ───

const CASH_KEY = 'desres_yawehs_cash'

export function getCashOnHand(date, dept = 'yawehs') {
  const key = dept === 'yawehs' ? CASH_KEY : `desres_${dept}_cash`
  const all = load(key)
  return all.filter(r => !date || r.date === date)
}

export function saveCashOnHand(date, cash, card, dept = 'yawehs') {
  const key = dept === 'yawehs' ? CASH_KEY : `desres_${dept}_cash`
  const col = dept === 'yawehs' ? 'yawehsCash' : `${dept}Cash`
  const all = load(key)
  const existing = all.findIndex(r => r.date === date)
  const record = {
    id: existing >= 0 ? all[existing].id : genId(),
    departmentId: dept,
    date,
    cash: Number(cash || 0),
    card: Number(card || 0),
    total: Number(cash || 0) + Number(card || 0),
    createdAt: new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(key, all)
  firestoreSet(col, record)
  return record
}

export function deleteExpenditureForDate(date, dept = 'yawehs') {
  const key = dept === 'yawehs' ? EXPENDITURE_KEY : `desres_${dept}_expenditure`
  const col = dept === 'yawehs' ? 'yawehsExpenditure' : `${dept}Expenditure`
  const all = load(key)
  const toDelete = all.filter(r => r.date === date)
  const remaining = all.filter(r => r.date !== date)
  save(key, remaining)
  for (const r of toDelete) firestoreDelete(col, r.id)
  return toDelete.length
}

export function deleteCashOnHandForDate(date, dept = 'yawehs') {
  const key = dept === 'yawehs' ? CASH_KEY : `desres_${dept}_cash`
  const col = dept === 'yawehs' ? 'yawehsCash' : `${dept}Cash`
  const all = load(key)
  const toDelete = all.filter(r => r.date === date)
  const remaining = all.filter(r => r.date !== date)
  save(key, remaining)
  for (const r of toDelete) firestoreDelete(col, r.id)
  return toDelete.length
}

// ─── Location Cash (cash on hand per distribution point) ───

const LOCATION_CASH_KEY = 'desres_location_cash'

export function getLocationCash(date, pointId, dept) {
  const all = load(LOCATION_CASH_KEY)
  return all.filter(r => r.departmentId === dept && r.date === date && (!pointId || r.pointId === pointId))
}

export function saveLocationCash(date, pointId, pointName, cash, card, dept) {
  const all = load(LOCATION_CASH_KEY)
  const existing = all.findIndex(r => r.departmentId === dept && r.date === date && r.pointId === pointId)
  const record = {
    id: existing >= 0 ? all[existing].id : genId(),
    departmentId: dept,
    date,
    pointId,
    pointName,
    cash: Number(cash || 0),
    card: Number(card || 0),
    total: Number(cash || 0) + Number(card || 0),
    createdAt: new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(LOCATION_CASH_KEY, all)
  firestoreSet('locationCash', record)
  return record
}

export function deleteLocationCash(date, pointId, dept) {
  const all = load(LOCATION_CASH_KEY)
  const toDelete = all.filter(r => r.departmentId === dept && r.date === date && r.pointId === pointId)
  const remaining = all.filter(r => !(r.departmentId === dept && r.date === date && r.pointId === pointId))
  save(LOCATION_CASH_KEY, remaining)
  for (const r of toDelete) firestoreDelete('locationCash', r.id)
  return toDelete.length
}

// ─── Daily Collections (bakery cash collected per distribution site) ───

const DAILY_COLLECTIONS_KEY = 'desres_daily_collections'

export function getDailyCollections(dept, date) {
  const all = load(DAILY_COLLECTIONS_KEY)
  return all.filter(r => r.departmentId === dept && (!date || r.date === date))
}

export function saveDailyCollection(dept, date, siteCode, data) {
  const all = load(DAILY_COLLECTIONS_KEY)
  const detId = `${dept}_coll_${date}_${siteCode}`.replace(/\s+/g, '_')
  const existing = all.findIndex(r => r.departmentId === dept && r.date === date && r.siteCode === siteCode)
  const record = {
    id: existing >= 0 ? all[existing].id : detId,
    departmentId: dept,
    date,
    siteCode,
    siteName: data.siteName || siteCode,
    projected: Number(data.projected) || 0,
    collected: Number(data.collected) || 0,
    difference: (Number(data.projected) || 0) - (Number(data.collected) || 0),
    notes: data.notes || '',
    createdAt: new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(DAILY_COLLECTIONS_KEY, all)
  firestoreSet('bakeryDailyCollections', record)
  return record
}

export function saveDailyCollectionsBatch(dept, date, entries) {
  const all = load(DAILY_COLLECTIONS_KEY)
  const toFirestore = []
  for (const entry of entries) {
    const detId = `${dept}_coll_${date}_${entry.siteCode}`.replace(/\s+/g, '_')
    const existing = all.findIndex(r => r.departmentId === dept && r.date === date && r.siteCode === entry.siteCode)
    const record = {
      id: existing >= 0 ? all[existing].id : detId,
      departmentId: dept,
      date,
      siteCode: entry.siteCode,
      siteName: entry.siteName || entry.siteCode,
      projected: Number(entry.projected) || 0,
      collected: Number(entry.collected) || 0,
      difference: (Number(entry.projected) || 0) - (Number(entry.collected) || 0),
      notes: entry.notes || '',
      createdAt: new Date().toISOString(),
    }
    if (existing >= 0) all[existing] = record
    else all.push(record)
    toFirestore.push(record)
  }
  save(DAILY_COLLECTIONS_KEY, all)
  firestoreBatchSet('bakeryDailyCollections', toFirestore)
}

// Get projected collection total for a site: sum of (qtySent - reportedSurplus)
// × unitPrice across distribution docs. Sites return surplus, so they only
// owe for what was sold, not what was sent.
//
// Price resolution priority:
//   1. doc.unitPrice — frozen at save time (preferred)
//   2. current product list — fallback for legacy docs without a frozen price
// Returns 0 contribution for unknown product codes (rather than masking
// the error with the old salesRands fallback).
//
// qtySold = max(0, qtySent - reportedSurplus). The Math.max guard handles
// the surplus-exceeds-sent edge case (data entry error) so a single bad row
// can't drag the projected total negative.
export function getBakeryProjectedForSite(dept, date, siteCode) {
  const logs = getDistributionLogs(date, siteCode, dept)
  // Use the live-cache + canonical-seed union so a stale products cache
  // (older Firestore mirror that pre-dates a seed update) cannot strand
  // the price lookup. Frozen doc.unitPrice still wins on rows that have it.
  const products = getProductsForLookup(dept)
  let total = 0
  for (const doc of logs) {
    const sent = Number(doc.qtySent ?? doc.qtyDistributed ?? 0)
    const surplus = Number(doc.reportedSurplus ?? doc.surplus ?? 0)
    const qtySold = Math.max(0, sent - surplus)
    // Resolve legacy product codes (e.g. BRD → BREAD) so the lookup
    // succeeds against the current canonical seed. The frozen unitPrice
    // on the doc still wins if present (Fix 1 priority).
    const resolvedCode = BAKERY_PRODUCT_CODE_ALIASES[doc.productCode] ?? doc.productCode
    const prod = products.find(p => p.code === resolvedCode)
    const price = Number(doc.unitPrice) || prod?.unitPrice || 0
    total += qtySold * price
  }
  return Math.round(total * 100) / 100
}

// One-shot migration: rewrite distribution docs whose productCode matches
// a key in BAKERY_PRODUCT_CODE_ALIASES. Updates productCode + recomputes
// the deterministic doc id, then writes the new doc to Firestore AND
// deletes the old Firestore doc — otherwise backgroundSyncForDate would
// re-inject the legacy code on the next page load.
//
// Run from the browser console:
//   await window.migrateBakeryProductCodes('bakery', true)   // dry-run
//   await window.migrateBakeryProductCodes('bakery', false)  // apply
//
// Returns an array of {docId, date, pointId, oldCode, newCode, qtySent,
// qtySold} describing every doc that was (or would be) migrated.
export async function migrateBakeryProductCodes(dept, dryRun = true) {
  const key = bakeryKey(dept, 'distribution')
  const collection = bakeryCollection(dept, 'distribution')
  const allDocs = JSON.parse(localStorage.getItem(key) || '[]')

  // Snapshot the docs that need migration BEFORE we mutate anything, so
  // we can also remember the old doc id for the Firestore delete step.
  const toMigrate = []
  for (const doc of allDocs) {
    const alias = BAKERY_PRODUCT_CODE_ALIASES[doc.productCode]
    if (alias) {
      toMigrate.push({
        oldDocId: doc.id,
        date: doc.date,
        pointId: doc.pointId,
        oldCode: doc.productCode,
        newCode: alias,
        qtySent: doc.qtySent,
        qtySold: doc.qtySold,
      })
    }
  }

  console.log(`[migrateBakeryProductCodes] Found ${toMigrate.length} docs with legacy codes`)
  if (toMigrate.length > 0) console.table(toMigrate)

  if (dryRun) {
    console.log('[migrateBakeryProductCodes] DRY RUN — no changes made.')
    console.log('[migrateBakeryProductCodes] Call migrateBakeryProductCodes("' + dept + '", false) to apply.')
    return toMigrate
  }

  if (toMigrate.length === 0) {
    console.log('[migrateBakeryProductCodes] Nothing to migrate.')
    return toMigrate
  }

  // Apply: walk allDocs in place, rewriting productCode + id for any
  // record whose code is in the alias map. Note we use departmentId from
  // the record itself (falling back to dept arg) to keep the id format
  // identical to saveDistributionBatch's deterministic format.
  for (const doc of allDocs) {
    const alias = BAKERY_PRODUCT_CODE_ALIASES[doc.productCode]
    if (!alias) continue
    doc.productCode = alias
    const recDept = doc.departmentId || dept
    doc.id = `${recDept}_${doc.date}_${doc.pointId}_${alias}`.replace(/\s+/g, '_')
  }

  // Persist locally first so the UI sees the new codes immediately.
  localStorage.setItem(key, JSON.stringify(allDocs))

  // Now reconcile Firestore: write the new doc, delete the old doc.
  // Both done sequentially per record so a partial failure leaves
  // localStorage and Firestore in sync.
  const newDocsById = {}
  for (const doc of allDocs) newDocsById[doc.id] = doc

  let written = 0
  let deleted = 0
  let failed = 0
  for (const m of toMigrate) {
    const newId = `${dept}_${m.date}_${m.pointId}_${m.newCode}`.replace(/\s+/g, '_')
    const newDoc = newDocsById[newId]
    if (!newDoc) {
      console.warn('[migrateBakeryProductCodes] new doc not found in local set after rewrite:', newId)
      failed++
      continue
    }
    try {
      await restSet(`${collection}/${newId}`, newDoc)
      written++
    } catch (err) {
      console.warn('[migrateBakeryProductCodes] write failed for', newId, err?.message)
      Sentry.captureException(err, { extra: { migration: 'bakery_codes', step: 'write', newId } })
      failed++
      continue
    }
    // Old doc id only differs from newId in the productCode segment.
    // Skip the delete if old and new ids are identical (defensive — they
    // should never be, given the alias map only contains rewrites).
    if (m.oldDocId && m.oldDocId !== newId) {
      try {
        await restDeleteDoc(`${collection}/${m.oldDocId}`)
        deleted++
      } catch (err) {
        console.warn('[migrateBakeryProductCodes] delete failed for', m.oldDocId, err?.message)
        Sentry.captureException(err, { extra: { migration: 'bakery_codes', step: 'delete', oldDocId: m.oldDocId } })
        failed++
      }
    }
  }

  console.log(`[migrateBakeryProductCodes] ✓ Migrated ${toMigrate.length} docs. Firestore writes: ${written}, deletes: ${deleted}, failed: ${failed}`)
  return toMigrate
}

// Returns the product codes referenced by this site's distribution docs
// that don't match any current product. Used by the UI to surface a
// warning when projected may be silently incomplete.
export function getMissingProductCodesForSite(dept, date, siteCode) {
  const logs = getDistributionLogs(date, siteCode, dept)
  // Same union as getBakeryProjectedForSite — without this, a stale
  // products cache makes valid canonical codes (BREAD, SBUNS_20, …) look
  // unknown and fires the amber warning incorrectly.
  const products = getProductsForLookup(dept)
  const missing = []
  for (const doc of logs) {
    // Resolve legacy aliases first so docs like BRD/BUNS/SCN are not
    // incorrectly flagged as missing — they map to canonical codes that
    // do exist in the current product seed. We still report the ORIGINAL
    // code in the missing list so staff can locate the offending row.
    const resolvedCode = BAKERY_PRODUCT_CODE_ALIASES[doc.productCode] ?? doc.productCode
    if (!products.find(p => p.code === resolvedCode)) {
      missing.push(doc.productCode)
    }
  }
  return missing
}

// ─── Credit Tracker (bakery outstanding balances per site/customer) ───

const CREDIT_KEY = 'desres_credit_entries'

export function getCreditEntries(dept) {
  const all = load(CREDIT_KEY)
  return all.filter(r => r.departmentId === dept)
}

export function saveCreditEntry(dept, entityCode, data) {
  const all = load(CREDIT_KEY)
  const detId = `${dept}_credit_${entityCode}`.replace(/\s+/g, '_')
  const existing = all.findIndex(r => r.departmentId === dept && r.entityCode === entityCode)
  const record = {
    id: existing >= 0 ? all[existing].id : detId,
    departmentId: dept,
    entityType: data.entityType || 'customer',
    entityCode,
    entityName: data.entityName || entityCode,
    openingBalance: Number(data.openingBalance) || 0,
    openingBalanceDate: data.openingBalanceDate || '',
    openingBalanceNote: data.openingBalanceNote || '',
    totalPaid: Number(data.totalPaid) || 0,
    lastUpdated: new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save(CREDIT_KEY, all)
  firestoreSet('bakeryCreditEntries', record)
  return record
}

export function deleteCreditEntry(dept, entityCode) {
  const all = load(CREDIT_KEY)
  const toDelete = all.find(r => r.departmentId === dept && r.entityCode === entityCode)
  if (toDelete) {
    save(CREDIT_KEY, all.filter(r => r.id !== toDelete.id))
    firestoreDelete('bakeryCreditEntries', toDelete.id)
    return 1
  }
  return 0
}

// ─── All data helpers ───

export function getAllDailySummaries(department) {
  return load(STORAGE_KEYS.dailySummary).filter(r => r.departmentId === department)
}

// ─── Migration Utility: Reassign misdated sales records ───
// Manually triggered only — moves sales records from wrongDate to correctDate
// for a given department. Returns count of records moved.
export function reassignSalesDate(department, wrongDate, correctDate) {
  if (!department || !wrongDate || !correctDate || wrongDate === correctDate) return 0
  const all = load(STORAGE_KEYS.sales)
  const toUpdate = []
  let count = 0

  for (const record of all) {
    if (record.departmentId === department && record.date === wrongDate) {
      record.date = correctDate
      toUpdate.push(record)
      count++
    }
  }

  if (count > 0) {
    save(STORAGE_KEYS.sales, all)
    firestoreBatchSet(COLLECTIONS.sales, toUpdate)
  }
  return count
}

// Scan for potentially misdated records: finds sales records in a given month
// that were imported via excel and returns them grouped by date for review
export function scanSalesForMonth(department, year, month) {
  const all = load(STORAGE_KEYS.sales)
  const prefix = `${year}-${String(month).padStart(2, '0')}`
  const byDate = {}
  for (const record of all) {
    if (record.departmentId === department && record.date?.startsWith(prefix)) {
      if (!byDate[record.date]) byDate[record.date] = []
      byDate[record.date].push(record)
    }
  }
  return byDate
}

// ─── Till Records ───

const TILL_KEY = 'desres_till_records'

export function getTillRecord(date) {
  const all = load(TILL_KEY)
  return all.find(r => r.date === date) || null
}

export function getAllTillRecords() {
  return load(TILL_KEY)
}

export function saveTillRecord(record) {
  const all = load(TILL_KEY)
  const existing = all.findIndex(r => r.date === record.date)
  const doc = {
    id: existing >= 0 ? all[existing].id : genId(),
    ...record,
    createdAt: new Date().toISOString(),
  }
  if (existing >= 0) all[existing] = doc
  else all.push(doc)
  save(TILL_KEY, all)
  firestoreSet('tillRecords', doc)
  return doc
}

export function deleteTillRecord(date) {
  const all = load(TILL_KEY)
  const rec = all.find(r => r.date === date)
  if (!rec) return 0
  save(TILL_KEY, all.filter(r => r.date !== date))
  firestoreDelete('tillRecords', rec.id)
  return 1
}

// ─── One-time dedup: remove duplicate sales from Firestore ───

export async function deduplicateSalesFirestore(department) {
  if (!department) { console.error('[DEDUP] department is required'); return { error: 'department required' } }
  const { restQuery: rq } = await import('./firestoreRest')

  // Get all unique dates from localStorage to know which dates to scan
  const localSales = load(STORAGE_KEYS.sales).filter(s => s.departmentId === department)
  const dates = [...new Set(localSales.map(s => s.date))].filter(Boolean).sort()
  console.log(`[DEDUP] Scanning ${dates.length} dates for dept: ${department}`)

  // Fetch from Firestore date by date
  let allSales = []
  for (const date of dates) {
    try {
      const results = await rq('sales', 'date', date)
      const deptResults = results.filter(s => s.departmentId === department)
      if (deptResults.length > 0) allSales.push(...deptResults)
    } catch (err) {
      console.warn(`[DEDUP] Failed to query date ${date}:`, err.message)
    }
  }

  const deptSales = allSales
  console.log(`[DEDUP] Found ${deptSales.length} total sales docs for dept: ${department || 'all'}`)

  // Group by dept + date + productCode
  const groups = {}
  for (const s of deptSales) {
    const k = `${s.departmentId}|${s.date}|${s.productCode}`
    if (!groups[k]) groups[k] = []
    groups[k].push(s)
  }

  const toDelete = []
  for (const [key, records] of Object.entries(groups)) {
    if (records.length <= 1) continue
    // Keep the newest by createdAt
    records.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
    const dupes = records.slice(1)
    console.log(`[DEDUP] ${key}: keeping 1, deleting ${dupes.length} duplicates`)
    toDelete.push(...dupes)
  }

  if (toDelete.length === 0) {
    console.log('[DEDUP] No duplicates found')
    return { deleted: 0, total: deptSales.length }
  }

  console.log(`[DEDUP] Deleting ${toDelete.length} duplicate records...`)
  let deleted = 0
  const { restDelete: restDel } = await import('./firestoreRest')
  for (let i = 0; i < toDelete.length; i += 10) {
    const batch = toDelete.slice(i, i + 10)
    await Promise.all(batch.map(d =>
      restDel(`sales/${d.id}`).then(() => deleted++).catch(err => console.warn('[DEDUP] delete failed:', d.id, err.message))
    ))
  }

  // Also clean localStorage
  const local = load(STORAGE_KEYS.sales)
  const deleteIds = new Set(toDelete.map(d => d.id))
  const cleaned = local.filter(r => !deleteIds.has(r.id))
  if (cleaned.length !== local.length) {
    save(STORAGE_KEYS.sales, cleaned)
    console.log(`[DEDUP] Cleaned ${local.length - cleaned.length} from localStorage`)
  }

  console.log(`[DEDUP] DONE: deleted ${deleted} duplicates, ${deptSales.length - deleted} remaining`)
  return { deleted, total: deptSales.length, remaining: deptSales.length - deleted }
}

// ─── Dedup utilities for morning logs and surplus ───

async function _deduplicateCollection(collectionName, storageKey, department, groupKeyFn) {
  const { restQuery: rq, restDelete: restDel } = await import('./firestoreRest')
  const localRecords = load(storageKey).filter(s => s.departmentId === department)
  const dates = [...new Set(localRecords.map(s => s.date))].filter(Boolean).sort()
  console.log(`[DEDUP] Scanning ${dates.length} dates in ${collectionName} for dept: ${department}`)

  let allDocs = []
  for (const date of dates) {
    try {
      const results = await rq(collectionName, 'date', date)
      const deptResults = results.filter(s => s.departmentId === department)
      if (deptResults.length > 0) allDocs.push(...deptResults)
    } catch (err) {
      console.warn(`[DEDUP] query ${date} failed:`, err.message)
      Sentry.captureException(err, { extra: { fn: 'deduplicateSalesFirestore.query', collectionName, department, date } })
    }
  }
  console.log(`[DEDUP] Found ${allDocs.length} total docs`)

  const groups = {}
  for (const d of allDocs) {
    const k = groupKeyFn(d)
    if (!groups[k]) groups[k] = []
    groups[k].push(d)
  }

  const toDelete = []
  for (const records of Object.values(groups)) {
    if (records.length <= 1) continue
    records.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
    const dupes = records.slice(1)
    toDelete.push(...dupes)
  }

  if (toDelete.length === 0) {
    console.log('[DEDUP] No duplicates found')
    return { deleted: 0, total: allDocs.length }
  }

  console.log(`[DEDUP] Deleting ${toDelete.length} duplicate records...`)
  let deleted = 0
  for (let i = 0; i < toDelete.length; i += 10) {
    const batch = toDelete.slice(i, i + 10)
    await Promise.all(batch.map(d =>
      restDel(`${collectionName}/${d.id}`).then(() => deleted++).catch(() => {})
    ))
  }

  // Clean localStorage
  const deleteIds = new Set(toDelete.map(d => d.id))
  const local = load(storageKey)
  const cleaned = local.filter(r => !deleteIds.has(r.id))
  if (cleaned.length !== local.length) {
    save(storageKey, cleaned)
    console.log(`[DEDUP] Cleaned ${local.length - cleaned.length} from localStorage`)
  }

  console.log(`[DEDUP] DONE: deleted ${deleted} duplicates, ${allDocs.length - deleted} remaining`)
  return { deleted, total: allDocs.length, remaining: allDocs.length - deleted }
}

export async function deduplicateMorningLogsFirestore(department) {
  if (!department) return { error: 'department required' }
  return _deduplicateCollection(COLLECTIONS.morningLog, STORAGE_KEYS.morningLog, department,
    d => `${d.departmentId}|${d.date}|${d.category}`)
}

export async function deduplicateSurplusFirestore(department) {
  if (!department) return { error: 'department required' }
  return _deduplicateCollection(COLLECTIONS.surplus, STORAGE_KEYS.surplus, department,
    d => `${d.departmentId}|${d.date}|${d.category}`)
}

// One-shot console utility for correcting a morning log pieceCost when
// the local cache is right but Firestore is stale (the 2026-04-04 steak
// scenario). Writes BOTH the Firestore doc AND the localStorage unit
// price cache so all devices converge on the corrected value after the
// next sync.
//
// Usage from DevTools:
//   await window.correctMorningLogPieceCost('kitchen', '2026-04-04', 'steak', 367, 10)
//   // Replace 367 (rands) and 10 (pieces) with the actual correct values
//
// Categories supported: 'steak', 'vors' (these are the only two whose
// unit price feeds the reconciliation engine via getDailyPrices).
export async function correctMorningLogPieceCost(dept, date, category, correctValue, correctPieces) {
  if (!dept || !date || !category) throw new Error('dept, date, category required')
  if (!(Number(correctValue) > 0) || !(Number(correctPieces) > 0)) {
    throw new Error('correctValue and correctPieces must be positive numbers')
  }
  const value = Number(correctValue)
  const openingPieces = Number(correctPieces)
  const pieceCost = Math.round((value / openingPieces) * 100) / 100
  const id = `${dept}_${date}_${category}`.replace(/\s+/g, '_')
  const record = {
    id,
    departmentId: dept,
    date,
    category,
    value,
    unit: 'rands',
    openingPieces,
    pieceCost,
    correctedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  }

  // 1. Push the corrected record to Firestore (blocking — we want to
  // confirm Firestore has the new value before touching local state).
  await restSet(`${COLLECTIONS.morningLog}/${id}`, record)

  // 2. Update the localStorage morning log entry in place.
  try {
    const all = load(STORAGE_KEYS.morningLog)
    const idx = all.findIndex(r => r.departmentId === dept && r.date === date && r.category === category)
    if (idx >= 0) {
      all[idx] = { ...all[idx], value, openingPieces, pieceCost }
    } else {
      all.push(record)
    }
    save(STORAGE_KEYS.morningLog, all)
  } catch { /* localStorage unavailable */ }

  // 3. Refresh the unit price cache that getDailyPrices reads.
  try {
    if (category === 'steak') {
      localStorage.setItem(`desres_steak_unit_price_${dept}_${date}`, String(pieceCost))
    } else if (category === 'vors') {
      localStorage.setItem(`desres_vors_unit_price_${dept}_${date}`, String(pieceCost))
    }
  } catch { /* localStorage unavailable */ }

  console.log(`✓ Corrected ${category} pieceCost to ${pieceCost} for ${dept} ${date} (value ${value}, pieces ${openingPieces})`)
  return { id, value, openingPieces, pieceCost }
}

// Set the data start date — reconciliation flags suppressed before this date
export async function setDataStartDate(dateStr) {
  const { restSet: rs } = await import('./firestoreRest')
  await rs('config/settings', { dataStartDate: dateStr })
  console.log(`[CONFIG] Data start date set to ${dateStr}`)
  return { dataStartDate: dateStr }
}

// Expose on window for manual use from DevTools console
if (typeof window !== 'undefined') {
  window.deduplicateSalesFirestore = deduplicateSalesFirestore
  window.deduplicateMorningLogsFirestore = deduplicateMorningLogsFirestore
  window.deduplicateSurplusFirestore = deduplicateSurplusFirestore
  window.setDataStartDate = setDataStartDate
  window.migrateBakeryProductCodes = migrateBakeryProductCodes
  window.BAKERY_PRODUCT_CODE_ALIASES = BAKERY_PRODUCT_CODE_ALIASES
  window.correctMorningLogPieceCost = correctMorningLogPieceCost
}
